














































































































































































































































































































































































import Vue from "vue";
import eventBus from "@/services/eventBus";
import dayjs from "dayjs";
import { GridColumnProps } from "@progress/kendo-vue-grid";
import { ValidationRules } from "@/models/Validation";
import { Accrual, AccrualAmount, AccrualBase, AccrualInvoiceItem } from "../models/Accrual";
import AccrualApi from "../services/AccrualApi";
import configuration from "@/models/Configuration";
import { getInvoiceItems } from "@/modules/invoiceItems/services/api";

// Components
import MoneyInput from "@/components/MoneyInput.vue";
import MoneyDisplay from "@/components/MoneyDisplay.vue";
import IconButton from "@/components/IconButton.vue";
import DateInput from "@/components/DateInput.vue";
import RelationDisplayName from "@/modules/invoiceItems/components/RelationDisplayName.vue";

const rules: ValidationRules = {
  date: [(v: string) => !!v || "Hibás dátum"],
};

interface Data {
  rules: ValidationRules;
  accrual: Accrual;
  base: AccrualBase;
  isItemLevel: boolean;
  invoiceItems: AccrualInvoiceItem[];
  savedItemAccruals: Accrual[];
  isDelete: boolean;
  loading: boolean;
}

interface Methods {
  loadInvoiceItems(): Promise<void>;
  saveItemAccruals(): Promise<boolean>;
  itemDigits(currency: string): number;
  itemMonths(row: AccrualInvoiceItem): number;
  generateItemBlocks(row: AccrualInvoiceItem): void;
  displayDate(date: string): string;
  generateItems(): void;
  deleteItem(e: AccrualAmount): void;
  save(): Promise<boolean>;
  removeAccrual(): void;
  toDecimal(value: number): number;
  defaultStartDate(): string;
  defaultEndDate(): string;
  emitItemCount(): void;
}

interface Computed {
  itemsTotal: number;
  itemsLeft: number;
  hasSavedAccrual: boolean;
  columns: GridColumnProps[];
}

interface Props {
  isPageDisabled: boolean;
  invoiceID: number;
  isPeriodic: boolean;
  periodStartDate: string;
  periodEndDate: string;
  prefix: string;
  accStatus: number;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    MoneyInput,
    MoneyDisplay,
    IconButton,
    DateInput,
    RelationDisplayName,
  },

  props: {
    isPageDisabled: { type: Boolean, default: false },
    invoiceID: { type: Number, default: 0 },
    isPeriodic: { type: Boolean, default: false },
    periodStartDate: { type: String, default: "" },
    periodEndDate: { type: String, default: "" },
    prefix: { type: String, default: "geni" },
    accStatus: { type: Number, default: 0 },
  },

  data: () => ({
    rules,
    accrual: new Accrual(),
    base: new AccrualBase(),
    isItemLevel: false,
    invoiceItems: [],
    savedItemAccruals: [],
    isDelete: false,
    loading: false,
  }),

  async beforeMount() {
    const invoiceID = Number(this.invoiceID) || 0;
    // elhatárolható összegek ÁFA-levonhatóság szerint (számla szintű + tételenkénti alapok)
    this.base = await AccrualApi.getBase(this.prefix, invoiceID);
    this.accrual = await AccrualApi.get(this.prefix, invoiceID);

    if (this.accrual.id == 0) {
      // még nincs elhatárolás -> dátumok előtöltése, összeg a számított alapból
      this.accrual.invNo = invoiceID;
      this.accrual.startDate = this.defaultStartDate();
      this.accrual.endDate = this.defaultEndDate();
      this.accrual.amount = this.base.total;
    }
    // mentett elhatárolásnál a fejben tárolt összeg jelenik meg (a fej tábla a forrás)

    // ha vannak tétel szintű mentett elhatárolások, tétel szintű módban indulunk
    this.savedItemAccruals = await AccrualApi.getItems(this.prefix, invoiceID);
    this.isItemLevel = this.savedItemAccruals.length > 0;

    this.emitItemCount();
  },

  watch: {
    async isItemLevel(value: boolean) {
      if (value) await this.loadInvoiceItems();
    },
  },

  methods: {
    // Tétel szintű elhatárolásnál a számla tételei, egyenként saját elhatárolási időszakkal
    async loadInvoiceItems() {
      if (this.invoiceItems.length > 0) return;

      const invoiceID = Number(this.invoiceID) || 0;
      if (invoiceID == 0) return;

      const items = await getInvoiceItems(invoiceID);
      this.invoiceItems = items.map(
        (i) => new AccrualInvoiceItem(i, this.accrual.startDate, this.accrual.endDate)
      );
      // mentett tétel szintű elhatárolások visszatöltése, a többi tételre alap kalkuláció
      this.invoiceItems.forEach((row) => {
        const saved = this.savedItemAccruals.find((a) => a.invItem == row.invItem);
        // a tétel elhatárolandó összege: mentett elhatárolásnál a tárolt fej összeg,
        // egyébként a számított alap (nettó + le nem vonható ÁFA-hányad, HUF-ban);
        // a nettoValue NEM használható fallbacknek, mert az a számla pénznemében van
        const base = this.base.items.find((b) => b.invoiceItemID == row.invItem);
        row.baseAmount = saved ? saved.amount : this.toDecimal(base?.amount ?? 0);
        // forintra átváltott nettó/bruttó a megjelenítéshez
        row.nettoHuf = base?.netto ?? 0;
        row.bruttoHuf = base?.brutto ?? 0;
        if (saved) {
          row.accrualId = saved.id;
          row.startDate = saved.startDate;
          row.endDate = saved.endDate;
          row.isDailyRate = saved.isDailyRate;
          row.items = saved.items;
        } else if (base) {
          this.generateItemBlocks(row);
        }
      });
    },

    itemDigits(currency) {
      if (configuration.operel) return 2;
      return configuration.getCurrDigits(currency)?.geniItemDigits ?? 0;
    },

    // A tétel időszaka által érintett hónapok száma (érvénytelen időszaknál 0)
    itemMonths(row) {
      const start = dayjs(row.startDate);
      const end = dayjs(row.endDate);
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) return 0;

      return end.startOf("month").diff(start.startOf("month"), "month") + 1;
    },

    // Havi bontás generálása a detail sorhoz — a fej kalkulációval azonos logikával:
    // napi bontásnál nap-arányosan, egyébként egyenlő havi részekkel,
    // a kerekítési különbözet az utolsó hónapra kerül.
    // A generált összegek utána szabadon szerkeszthetők.
    generateItemBlocks(row) {
      // kerekítés tételt sosem határolunk el, bármilyen összeggel; egyéb 0 összegű tételnél
      // marad a normál (0 Ft-os blokkra hibát jelző) viselkedés
      if (row.item?.isRoundingItem) {
        row.items = [];
        return;
      }

      const start = dayjs(row.startDate);
      const end = dayjs(row.endDate);
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
        row.items = [];
        return;
      }

      const blocks: { start: dayjs.Dayjs; end: dayjs.Dayjs }[] = [];
      let month = start.startOf("month");
      while (!month.isAfter(end)) {
        const blockStart = month.isBefore(start) ? start : month;
        const monthEnd = month.endOf("month");
        blocks.push({ start: blockStart, end: monthEnd.isAfter(end) ? end : monthEnd });
        month = month.add(1, "month");
      }

      const totalDays = end.diff(start, "day") + 1;
      const items: AccrualAmount[] = [];
      let allocated = 0;

      blocks.forEach((b, i) => {
        const daysInBlock = b.end.diff(b.start, "day") + 1;

        let amount = row.isDailyRate
          ? this.toDecimal((row.baseAmount * daysInBlock) / totalDays)
          : this.toDecimal(row.baseAmount / blocks.length);

        if (i == blocks.length - 1) {
          amount = this.toDecimal(row.baseAmount - allocated);
        }
        allocated = this.toDecimal(allocated + amount);

        items.push(
          new AccrualAmount({
            accDate: b.start.format("YYYY-MM-DD"),
            amount: amount,
          })
        );
      });

      row.items = items;
    },

    displayDate(date) {
      return dayjs(date).format("YYYY. MM. DD.");
    },

    toDecimal(value) {
      return Math.round((value + Number.EPSILON) * 100) / 100;
    },

    // A mentett elhatárolás tételszámának jelzése a szülő felé (tab-felirat)
    emitItemCount() {
      if (this.isItemLevel) {
        this.$emit(
          "accrualItemCountChanged",
          this.savedItemAccruals.reduce((acc, a) => acc + a.items.length, 0)
        );
        return;
      }
      this.$emit("accrualItemCountChanged", this.accrual.id == 0 ? 0 : this.accrual.items.length);
    },

    // Időszakos elszámolású ügyletnél a teljesítés kezdő dátuma, egyébként az aktuális dátum
    defaultStartDate() {
      if (this.isPeriodic && dayjs(this.periodStartDate).isValid()) {
        return dayjs(this.periodStartDate).format("YYYY-MM-DD");
      }
      return dayjs().format("YYYY-MM-DD");
    },

    // Időszakos elszámolású ügyletnél a teljesítés vég dátuma, egyébként az aktuális dátum
    defaultEndDate() {
      if (this.isPeriodic && dayjs(this.periodEndDate).isValid()) {
        return dayjs(this.periodEndDate).format("YYYY-MM-DD");
      }
      return dayjs().format("YYYY-MM-DD");
    },

    generateItems() {
      if (this.isPageDisabled) return;

      if (this.accrual.amount == 0) {
        eventBus.$emit("warn", "Az elhatárolandó összeg nem lehet nulla!");
        return;
      }

      if (Math.abs(this.accrual.amount) > Math.abs(this.base.total)) {
        eventBus.$emit("warn", "Az elhatárolandó összeg nem lehet nagyobb az elhatárolható összegnél!");
        return;
      }

      const start = dayjs(this.accrual.startDate);
      const end = dayjs(this.accrual.endDate);

      if (!start.isValid() || !end.isValid()) {
        eventBus.$emit("warn", "Hibás kezdő vagy vég dátum!");
        return;
      }

      if (end.isBefore(start)) {
        eventBus.$emit("warn", "A kezdő dátum későbbi mint a vég dátum!");
        return;
      }

      this.accrual.items = [];

      // Tételek az időszak által érintett hónapok szerint (havonta egy tétel)
      const blocks: { start: dayjs.Dayjs; end: dayjs.Dayjs }[] = [];
      let month = start.startOf("month");
      while (!month.isAfter(end)) {
        const blockStart = month.isBefore(start) ? start : month;
        const monthEnd = month.endOf("month");
        blocks.push({ start: blockStart, end: monthEnd.isAfter(end) ? end : monthEnd });
        month = month.add(1, "month");
      }

      const totalDays = end.diff(start, "day") + 1;
      const items: AccrualAmount[] = [];
      let allocated = 0;

      blocks.forEach((b, i) => {
        // Az adott blokkra eső napok az időszakon belül
        const daysInBlock = b.end.diff(b.start, "day") + 1;

        let amount = this.accrual.isDailyRate
          ? this.toDecimal((this.accrual.amount * daysInBlock) / totalDays)
          : this.toDecimal(this.accrual.amount / blocks.length);

        // Kerekítésből eredő különbözet az utolsó tételre
        if (i == blocks.length - 1) {
          amount = this.toDecimal(this.accrual.amount - allocated);
        }
        allocated = this.toDecimal(allocated + amount);

        items.push(
          new AccrualAmount({
            accDate: b.start.format("YYYY-MM-DD"),
            amount: amount,
          })
        );
      });

      this.accrual.items = items;
    },

    deleteItem(e) {
      if (this.isPageDisabled) return;

      const index = this.accrual.items.indexOf(e);
      if (index > -1) this.accrual.items.splice(index, 1);
    },

    
    async save() {
      if (this.loading) return false;

      if (this.isItemLevel) return this.saveItemAccruals();

      if (Math.abs(this.accrual.amount) > Math.abs(this.base.total)) {
        eventBus.$emit("error", "Az elhatárolandó összeg nem lehet nagyobb az elhatárolható összegnél!");
        return false;
      }

      if (this.accrual.items.some((i) => !i.amount)) {
        eventBus.$emit("error", "Egy elhatárolás tétel összege sem lehet nulla!");
        return false;
      }
      if (this.accrual.items.length > 0 && this.itemsLeft > 0) {
        eventBus.$emit("error", "Az elhatárolás nem menthető, amíg a maradék nagyobb mint nulla!");
        return false;
      } 
      if (this.accrual.items.length == 0) {
        eventBus.$emit("error", "Az elhatárolás nem menthető, mert nem történt kalkuláció!");
        return false;
      }

      if (this.accrual.items.length > 0 && this.itemsTotal != this.accrual.amount) {
        eventBus.$emit("error", "A tételek összege nem egyezik az elhatárolandó összeggel!");
        return false;
      }

      this.loading = true;
      try {
        this.accrual.invNo = Number(this.invoiceID) || 0;
        // számla szintű elhatárolásnál nincs tételkötés
        if (!this.isItemLevel) this.accrual.invItem = 1;
        // a válaszban a fejben tárolt összeg jön vissza (a fej tábla a forrás)
        this.accrual = await AccrualApi.manage(this.prefix, this.accrual);
        eventBus.$emit("success", "Sikeres elhatárolás mentés!");
        this.emitItemCount();
        return true;
      } finally {
        this.loading = false;
      }
    },

    // Tétel szintű mentés: minden érvényes havi bontással rendelkező tétel külön elhatárolásként mentődik.
    // A bontás nélküli tételek mentett elhatárolása (ha volt) törlődik a backenden.
    async saveItemAccruals() {
      const accrued = this.invoiceItems.filter((r) => r.items.length > 0);

      if (accrued.length == 0) {
        eventBus.$emit("error", "Az elhatárolás nem menthető, mert nem történt kalkuláció!");
        return false;
      }

      for (const row of accrued) {
        if (row.items.some((i) => !i.amount)) {
          eventBus.$emit("error", `Egy elhatárolás tétel összege sem lehet nulla! (${row.item.itemName})`);
          return false;
        }

        const rowTotal = this.toDecimal(row.items.reduce((acc, i) => (acc += i?.amount ?? 0), 0));
        if (rowTotal != this.toDecimal(row.baseAmount)) {
          eventBus.$emit("error", `A havi bontás összege nem egyezik a tétel elhatárolandó összegével! (${row.item.itemName})`);
          return false;
        }
      }

      this.loading = true;
      try {
        const invoiceID = Number(this.invoiceID) || 0;
        const payload = accrued.map(
          (row) =>
            new Accrual({
              id: row.accrualId,
              invNo: invoiceID,
              invItem: row.invItem,
              amount: row.baseAmount,
              startDate: row.startDate,
              endDate: row.endDate,
              isDailyRate: row.isDailyRate,
              items: row.items,
            })
        );

        this.savedItemAccruals = await AccrualApi.manageItems(this.prefix, invoiceID, payload);

        // mentett azonosítók visszatöltése a sorokba
        this.invoiceItems.forEach((row) => {
          const saved = this.savedItemAccruals.find((a) => a.invItem == row.invItem);
          if (saved) {
            row.accrualId = saved.id;
            row.items = saved.items;
          } else {
            row.accrualId = 0;
          }
        });

        eventBus.$emit("success", "Sikeres elhatárolás mentés!");
        this.emitItemCount();
        return true;
      } finally {
        this.loading = false;
      }
    },

    async removeAccrual() {
      if (this.loading) return;

      this.loading = true;
      try {
        // a save() validációit kihagyva, üres tétellistával -> a backend törli az elhatárolást és a kontírozást is
        const payload = new Accrual({
          ...this.accrual,
          invNo: Number(this.invoiceID) || 0,
          items: [],
        });
        await AccrualApi.manage(this.prefix, payload, true);
        eventBus.$emit("success", "Sikeres elhatárolás törlés!");

        this.accrual = new Accrual({
          invNo: Number(this.invoiceID) || 0,
          amount: this.base.total,
          startDate: this.defaultStartDate(),
          endDate: this.defaultEndDate(),
        });
        // a backend a tétel szintű elhatárolásokat is törölte
        this.savedItemAccruals = [];
        this.invoiceItems.forEach((row) => (row.accrualId = 0));
        this.isItemLevel = false;
        this.isDelete = false;
        this.emitItemCount();
        this.$emit("accountingDeleted");
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    itemsTotal() {
      // Tétel szintnél a tételek havi bontásainak (szerkeszthető) összegei számítanak elhatároltnak
      if (this.isItemLevel) {
        return this.toDecimal(
          this.invoiceItems.reduce(
            // a kerekítés tételnek nincs havi bontása, de az összege elszámoltnak számít
            (acc, r) =>
              (acc += r.item?.isRoundingItem
                ? r.baseAmount
                : r.items.reduce((a, b) => (a += b?.amount ?? 0), 0)),
            0
          )
        );
      }
      return this.toDecimal(this.accrual.items.reduce((acc, current) => (acc += current?.amount ?? 0), 0));
    },

    itemsLeft() {
      return this.toDecimal(this.accrual.amount - this.itemsTotal);
    },

    hasSavedAccrual() {
      return this.isItemLevel ? this.savedItemAccruals.length > 0 : this.accrual.id != 0;
    },

    columns: () => [
      // Törlés oszlop - egyelőre elrejtve, később visszatehető
      // {
      //   title: "",
      //   cell: "command",
      //   width: "55x",
      // },
      {
        field: "accDate",
        title: "Dátum",
        cell: "accDate",
      },
      {
        field: "amount",
        title: "Összeg",
        cell: "amount",
        width: "185px",
      },
      { width: 0 },
    ],
  },
});
